import React, { forwardRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { color, border, shadow } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useDefaultRef } from '../utils/useDefaultRef';
import Popper from '../popper/Popper';
import { Box } from '../grid/Grid';

const Wrap = styled(Box)`
  font-size: 13px;
  line-height: 1.54;
  box-sizing: border-box;
  ${color}
  ${border}
  ${shadow}
`;

const Arrow = styled.span`
  position: absolute;
  z-index: -1;
  ${color}
`;

const StyledPopper = styled(Popper)`
  z-index: ${themeGet('zIndex.tooltip')};

  &[x-placement*='top'] ${Wrap} {
    margin-bottom: 8px;
  }

  &[x-placement*='bottom'] ${Wrap} {
    margin-top: 8px;
  }

  &[x-placement*='right'] ${Wrap} {
    margin-left: 8px;
  }

  &[x-placement*='left'] ${Wrap} {
    margin-right: 8px;
  }

  &[x-placement*='bottom'] ${Arrow} {
    top: 4px;
    left: 0;
    margin-top: 0;
    margin-left: 4px;
    margin-right: 4px;

    &::before {
      content: '';
      display: block;
      width: 0;
      height: 0;
      margin: 0;
      border-style: solid;
      border-width: 0 4px 4px 4px;
      border-color: transparent transparent currentcolor transparent;
    }
  }

  &[x-placement*='top'] ${Arrow} {
    bottom: 4px;
    left: 0;
    margin-bottom: 0;
    margin-left: 4px;
    margin-right: 4px;

    &::before {
      content: '';
      display: block;
      width: 0;
      height: 0;
      margin: 0;
      border-style: solid;
      border-width: 4px 4px 0 4px;
      border-color: currentcolor transparent transparent transparent;
    }
  }

  &[x-placement*='right'] ${Arrow} {
    left: 4px;
    margin-left: 0;
    margin-top: 4px;
    margin-bottom: 4px;

    &::before {
      content: '';
      display: block;
      width: 0;
      height: 0;
      margin: 0;
      border-style: solid;
      border-width: 4px 4px 4px 0;
      border-color: transparent currentcolor transparent transparent;
    }
  }

  &[x-placement*='left'] ${Arrow} {
    right: 4px;
    margin-right: 0;
    margin-top: 4px;
    margin-bottom: 4px;

    &::before {
      content: '';
      display: block;
      width: 0;
      height: 0;
      margin: 0;
      border-style: solid;
      border-width: 4px 0 4px 4px;
      border-color: transparent transparent transparent currentcolor;
    }
  }
`;

export const Tooltip = forwardRef(function Tooltip(props) {
  const { title = '', arrow = false, placement, open: propOpen, children, ...rest } = props;
  const [ownRef, setOwnRef] = useDefaultRef(children.ref);
  const [arrowRef, setArrowRef] = useState(null);
  const [open, setOpen] = useState(propOpen);
  const popperOptions = useMemo(() => {
    return {
      modifiers: {
        arrow: {
          enabled: arrow,
          element: arrowRef,
        },
      },
    };
  }, [arrow, arrowRef]);

  return (
    <>
      {React.cloneElement(children, {
        ref: setOwnRef,
        onMouseOver: useCallback(() => {
          if (!open) {
            setOpen(true);
          }
        }, [setOpen, open]),
        onMouseLeave: useCallback(() => {
          if (open) {
            setOpen(false);
          }
        }, [setOpen, open]),
      })}
      <StyledPopper
        referRef={ownRef}
        open={open || propOpen || false}
        placement={placement}
        popperOptions={popperOptions}
      >
        <Wrap px="8px" py="4px" color="white" bg="#464a52" borderRadius="5px" maxWidth="300px" {...rest}>
          {title}
          {arrow && <Arrow color={rest.bg || rest.backgroundColor || 'black'} ref={setArrowRef}></Arrow>}
        </Wrap>
      </StyledPopper>
    </>
  );
});
